import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Camera, MapPin, Upload, Loader2 } from 'lucide-react';
import { Issue } from '@/types';
import { locationService } from '@/lib/location';
import { toast } from 'sonner';

interface IssueFormProps {
  onSubmit: (issue: Omit<Issue, 'id' | 'reportedAt' | 'updatedAt'>) => void;
  isSubmitting?: boolean;
}

/* =======================
   SMART HELPERS
   ======================= */

// Risk badge meta
function getRiskMeta(score: number) {
  if (score >= 70) return { label: 'High Risk', color: 'bg-red-100 text-red-700 border-red-300' };
  if (score >= 40) return { label: 'Medium Risk', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' };
  return { label: 'Low Risk', color: 'bg-green-100 text-green-700 border-green-300' };
}

// Risk scoring logic (rule-based prototype)
function calculateRiskScore(
  priority: Issue['priority'],
  category: Issue['category']
): number {
  let score = 30;

  if (priority === 'high') score += 40;
  if (priority === 'medium') score += 20;

  if (category === 'pothole') score += 20;
  if (category === 'street_light') score += 15;
  if (category === 'garbage') score += 10;

  return Math.min(score, 100);
}

// Mock AI-based category detection (prototype)
function detectCategoryFromImage(fileName: string): Issue['category'] {
  const name = fileName.toLowerCase();

  if (name.includes('road') || name.includes('pothole')) return 'pothole';
  if (name.includes('garbage') || name.includes('waste')) return 'garbage';
  if (name.includes('light') || name.includes('lamp')) return 'street_light';
  if (name.includes('sign')) return 'traffic_sign';

  return 'other';
}

/* =======================
   COMPONENT
   ======================= */

export default function IssueForm({ onSubmit, isSubmitting = false }: IssueFormProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '' as Issue['category'] | '',
    priority: 'medium' as Issue['priority'],
    reportedBy: ''
  });

  const [location, setLocation] = useState<{ lat: number; lng: number; address?: string } | null>(null);
  const [image, setImage] = useState<string>('');
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* ===== Location ===== */
  const getCurrentLocation = async () => {
    setIsLoadingLocation(true);
    try {
      const currentLocation = await locationService.getCurrentPosition();
      setLocation(currentLocation);
      toast.success('Location captured successfully');
    } catch {
      toast.error('Failed to get location. Please enable GPS.');
    } finally {
      setIsLoadingLocation(false);
    }
  };

  /* ===== Image + AI ===== */
  const handleImageCapture = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const suggestedCategory = detectCategoryFromImage(file.name);

    setFormData((prev) => ({
      ...prev,
      category: prev.category || suggestedCategory
    }));

    toast.success('Category auto-detected using AI (prototype)');

    const reader = new FileReader();
    reader.onload = (e) => setImage(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  /* ===== Submit ===== */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) return toast.error('Please enter an issue title');
    if (!formData.category) return toast.error('Please select a category');
    if (!location) return toast.error('Please capture your location');
    if (!image) return toast.error('Please upload an image');

    const riskScore = calculateRiskScore(
      formData.priority,
      formData.category as Issue['category']
    );

    onSubmit({
      title: formData.title.trim(),
      description: formData.description.trim(),
      category: formData.category as Issue['category'],
      priority: formData.priority,
      status: 'pending',
      location,
      image,
      reportedBy: formData.reportedBy.trim() || 'Anonymous User',
      riskScore
    });
  };

  const riskScorePreview =
    formData.category &&
    calculateRiskScore(formData.priority, formData.category as Issue['category']);

  const riskMeta = riskScorePreview ? getRiskMeta(riskScorePreview) : null;

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Camera className="h-5 w-5" />
          <span>Report a Street Issue</span>
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Reports are geo-tagged, time-stamped, and securely logged
        </p>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-8 p-6">

          {/* ===== Issue Details ===== */}
          <h3 className="section-title">Issue Details</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              placeholder="Issue title *"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            />
            <Input
              placeholder="Your name (optional)"
              value={formData.reportedBy}
              onChange={(e) => setFormData({ ...formData, reportedBy: e.target.value })}
            />
          </div>

          <Textarea
            placeholder="Describe the issue"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />

          {/* ===== Classification & Risk ===== */}
          <h3 className="section-title">Classification & Risk</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              value={formData.category}
              onValueChange={(v) => setFormData({ ...formData, category: v as Issue['category'] })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Category (AI-assisted)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pothole">Pothole</SelectItem>
                <SelectItem value="street_light">Street Light</SelectItem>
                <SelectItem value="garbage">Garbage</SelectItem>
                <SelectItem value="traffic_sign">Traffic Sign</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={formData.priority}
              onValueChange={(v) => setFormData({ ...formData, priority: v as Issue['priority'] })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {riskMeta && (
            <div className={`rounded-lg border p-4 ${riskMeta.color}`}>
              <div className="flex justify-between">
                <span className="font-semibold">{riskMeta.label}</span>
                <span className="font-bold">{riskScorePreview}/100</span>
              </div>
              <p className="text-xs mt-1">Auto-calculated risk score</p>
            </div>
          )}

          {/* ===== Location ===== */}
          <h3 className="section-title">Location Information</h3>

          <Button type="button" variant="outline" onClick={getCurrentLocation} disabled={isLoadingLocation}>
            {isLoadingLocation ? <Loader2 className="animate-spin mr-2" /> : <MapPin className="mr-2" />}
            Capture Current Location
          </Button>

          {location && (
            <p className="text-sm text-green-700">
              📍 {location.address || `${location.lat}, ${location.lng}`}
            </p>
          )}

          {/* ===== Evidence ===== */}
          <h3 className="section-title">Evidence</h3>

          <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}>
            <Upload className="mr-2" /> Upload Photo
          </Button>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleImageCapture}
            className="hidden"
          />

          {image && (
            <div className="relative rounded-xl overflow-hidden border shadow-sm">
              <img src={image} alt="Issue evidence" className="w-full h-48 object-cover" />
              <span className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                Evidence Photo
              </span>
            </div>
          )}

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Forwarding to Municipal Authority...' : 'Submit Issue Report'}
          </Button>

        </form>
      </CardContent>
    </Card>
  );
}

/* ===== Utility ===== */
const sectionTitle =
  'text-sm font-semibold text-muted-foreground uppercase tracking-wide';
